import { useState } from 'react';
import { css } from '@emotion/react';
import {
  create,
  parseCreationOptionsFromJSON,
  RegistrationPublicKeyCredential,
} from '@github/webauthn-json/browser-ponyfill';
import {
  AuthService,
  BeginWebAuthnRegistrationResponse,
  CompleteWebAuthnRegistrationRequest,
  CompleteWebAuthnRegistrationResponse,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { getWeaveToken } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, FormRow, TextField } from '@frontend/design-system';

const backendApi = appConfig.BACKEND_API;

const wrappedFetch = (token: string): ((url: string, reqInit: RequestInit) => Promise<Response>) => {
  return (url: string, reqInit: RequestInit) => {
    if (!!token) {
      reqInit.headers = {
        ...reqInit.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return fetch(`${backendApi}${url}`, reqInit).then((res) => {
      if (res.status !== 200) {
        return res.json().then((resBody: { code: number; message: string }) => {
          throw new Error(resBody.message);
        });
      }
      return res.json();
    });
  };
};

export const WebauthnRegistrationComponent = () => {
  const { t } = useTranslation('SignIn');
  const [passkey, setPasskey] = useState('');
  const [passkeyErrorMessage, setPasskeyErrorMessage] = useState('');

  const handleBeginWebauthnRegistration = async () => {
    const weaveJWT = getWeaveToken();
    if (!weaveJWT) {
      throw new Error('No weave token found');
    }
    return AuthService.BeginWebAuthnRegistration(
      wrappedFetch(weaveJWT) as (url: string, reqInit: RequestInit) => Promise<BeginWebAuthnRegistrationResponse>,
      {}
    ).then((data) => {
      let jsonOptions = '';

      if (data.options) {
        jsonOptions = atob(data.options);
      }

      const parsedOptions = JSON.parse(jsonOptions);
      const options = parseCreationOptionsFromJSON(parsedOptions);

      // webauthn registration challenge
      return create(options)
        .then((credential: RegistrationPublicKeyCredential) => {
          return handleCompleteWebauthnRegistration({
            sessionId: data.sessionId,
            credential: JSON.stringify(credential),
            credentialDisplayName: passkey,
          });
        })
        .catch((err: Error) => {
          console.error(err);
          setPasskeyErrorMessage('Error registering passkey');
        });
    });
  };

  const handleCompleteWebauthnRegistration = async (req: CompleteWebAuthnRegistrationRequest) => {
    const weaveJWT = getWeaveToken();
    if (!weaveJWT) {
      throw new Error('No weave token found');
    }
    return AuthService.CompleteWebAuthnRegistration(
      wrappedFetch(weaveJWT) as (url: string, reqInit: RequestInit) => Promise<CompleteWebAuthnRegistrationResponse>,
      req
    ).then((data) => {
      return data;
    });
  };

  return (
    <div>
      <FormRow
        css={css`
          text-align: initial;
          margin: ${theme.spacing(4, 0)};
        `}
      >
        {/*<input name='passkey' placeholder='Name your Passkey' value={passkey} onChange={passkeyInputChange}></input>*/}
        <TextField
          active={true}
          aria-invalid={false}
          error={passkeyErrorMessage}
          touched={!!passkey.length}
          onChange={(e) => {
            // @ts-ignore - FCE event is not properly typed
            setPasskey(e?.target.value); // <-- this works fine in the browser but not in the typescript
          }}
          value={passkey}
          label={t('Passkey name')}
          name={'passkey'}
          id={'passkey-input'}
          onBlur={() => {}}
          onFocus={() => {}}
        ></TextField>
      </FormRow>

      <FormRow
        css={css`
          text-align: initial;
          margin: ${theme.spacing(4, 0)};
        `}
      >
        <Button
          onClick={handleBeginWebauthnRegistration}
          disabled={!passkey.length}
          size='large'
          type='submit'
          css={{ width: '100%' }}
          variant='primary'
        >
          Register New Passkey
        </Button>
      </FormRow>
    </div>
  );
};
